import { collection, limit, orderBy, query, type Unsubscribe } from '@firebase/firestore';
import { httpsCallable } from '@firebase/functions';
import { db, functions } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { mapDocs } from '@utils/firestore';
import { PlanAssignmentUpdateSchema } from '../schemas/firestoreSchemas';
import type { PlanAssignmentUpdate } from '../types';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('api:defensePlanUpdates');

// The plan owner's side of the Sept 2026 update loop: asking a party for
// progress, rolling reported updates into one News post, and mailing that
// post back out. The three Cloud Functions behind these calls are documented
// in functions/src/plans/{requestPlanUpdate,publishPlanUpdateNews,
// sendCommunityUpdate}.ts — this file only mirrors their input/output shapes,
// the same way src/api/invites.ts mirrors validateInviteCode/redeemInviteCode.

function assignmentUpdatesCollection(planId: string) {
  return collection(
    db,
    COLLECTIONS.DEFENSE_PLANS,
    planId,
    COLLECTIONS.DEFENSE_PLAN_ASSIGNMENT_UPDATES
  );
}

export interface RequestPlanUpdateResult {
  sent: boolean;
  /** Why nothing went out, when nothing did. */
  reason?: 'no-email' | 'no-assignments' | 'suppressed' | 'send-failed';
  taskCount: number;
}

/**
 * "Ask for an update": emails one party the signed link listing their work
 * on this plan. Owner only (enforced server side). A rejected plan-private
 * or ask-cooldown case throws a FirebaseError with code
 * 'functions/failed-precondition' and a message meant to be shown as-is —
 * see requestPlanUpdate.ts's two throw sites.
 */
export async function requestPlanUpdate(
  planId: string,
  partyId: string
): Promise<RequestPlanUpdateResult> {
  const callable = httpsCallable<{ planId: string; partyId: string }, RequestPlanUpdateResult>(
    functions,
    'requestPlanUpdate'
  );
  const result = await callable({ planId, partyId });
  return result.data;
}

export interface PublishPlanUpdateNewsResult {
  postId: string;
  published: number;
}

/** Rolls the given assignmentUpdates into one News post. Owner only. */
export async function publishPlanUpdateNews(
  planId: string,
  updateIds: string[],
  title?: string
): Promise<PublishPlanUpdateNewsResult> {
  const callable = httpsCallable<
    { planId: string; updateIds: string[]; title?: string },
    PublishPlanUpdateNewsResult
  >(functions, 'publishPlanUpdateNews');
  const result = await callable({ planId, updateIds, title });
  return result.data;
}

export interface SendCommunityUpdateResult {
  parties: number;
  members: number;
  suppressed: number;
}

/** Mails a just-published News post to the plan's parties. Owner only. */
export async function sendCommunityUpdate(
  planId: string,
  postId: string,
  includeMembers?: boolean
): Promise<SendCommunityUpdateResult> {
  const callable = httpsCallable<
    { planId: string; postId: string; includeMembers?: boolean },
    SendCommunityUpdateResult
  >(functions, 'sendCommunityUpdate');
  const result = await callable({ planId, postId, includeMembers });
  return result.data;
}

/**
 * The plan owner's feed of reported progress: newest 50 assignmentUpdates,
 * newest first. Read-only to every client (rules: readable where the plan
 * is readable, `allow write: if false` — only the Cloud Functions above
 * ever write here), so this is the one read shape, no paging.
 */
export function subscribeAssignmentUpdates(
  planId: string,
  onNext: (updates: Record<string, PlanAssignmentUpdate>) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return onSnapshotWithRetry(
    query(assignmentUpdatesCollection(planId), orderBy('createdAt', 'desc'), limit(50)),
    (snap) => {
      const list = mapDocs(snap, PlanAssignmentUpdateSchema);
      onNext(Object.fromEntries(list.map((u) => [u.id, u])));
    },
    (error) => {
      log.error('subscribeAssignmentUpdates failed:', error);
      onError?.(error);
    }
  );
}
